import { ArrowRight } from "lucide-react";
import { AV_ICON, Bars } from "@/components/c360/C360Table";
import { EditButton, TileRow } from "@/components/c360/profile/VerdictBox";
import { RATING } from "@/lib/creator360";
import { VIEW_TAB, agoWords, liveLast, rankedAvenues, whyLine } from "@/lib/c360Profile";
import { CYAN, changedLine, rWord } from "@/lib/c360Mine";
import { botLineText } from "@/lib/botReasons";
import { useBots } from "@/components/c360/profile/Header";

const BOT_LINE = { yt: "YouTube bot check", live: "Live bot check", ig: "Instagram bot check", tg: "Telegram bot check" };

// v80 — the bot line: for a client only once they opened this creator's bot check in this session.
function footer(av, d, bots) {
  const out = [];
  if (d.bot_card && bots) out.push(`${BOT_LINE[av]}: ${botLineText(av, bots[av])}`);
  if (av === "live" && liveLast(d)) out.push(liveLast(d));
  if (av === "tg" && d.nums.tg.last_days != null) out.push(`Last post ${agoWords(d.nums.tg.last_days)}`);
  return out;
}

function cardStyle(mr, r) {
  if (mr) return { border: `1px solid ${CYAN}77`, background: "rgba(0,240,255,0.05)" };
  if (r === "unknown") return { border: "1px dashed rgba(113,113,122,0.5)" };
  return { border: `1px solid ${RATING[r].color}55`, background: `${RATING[r].color}0b` };
}

// One card per avenue; a BD's own verdict shows in cyan with KOL Radar's kept.
function AvenueCard({ av, i, d, bots, onOpen, onEdit }) {
  const x = d.av[av];
  const m = d.mine?.[av];
  const mr = m?.r;
  const shown = mr || x.r;
  const R = RATING[shown];
  const color = mr ? CYAN : R.color;
  const Icon = AV_ICON[av];
  const unknown = shown === "unknown";
  const why = whyLine(av, d);
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(av)} onKeyDown={(e) => e.key === "Enter" && onOpen(av)} data-testid={`c360-best-${av}`} data-rating={x.r} data-mine={mr || undefined}
      onContextMenu={(e) => { e.preventDefault(); onEdit(av); }}
      className="rounded-lg p-5 cursor-pointer transition-transform duration-150 hover:-translate-y-0.5 c360-rise flex flex-col" style={{ animationDelay: `${i * 70}ms`, ...cardStyle(mr, x.r) }}>
      <div className="flex items-center gap-3">
        <span className="mono text-xs text-zinc-500">#{i + 1}</span>
        <Icon size={16} style={{ color: unknown ? "#71717A" : color }} />
        <span className="text-white font-medium">{VIEW_TAB[av]}</span>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-2 py-0.5 rounded" style={{ color, background: `${color}14` }} data-testid={`c360-best-${av}-rating`}><Bars rating={shown} size={13} color={mr ? CYAN : undefined} />{R.label}{mr ? " · your call" : ""}</span>
        <span className="ml-auto"><EditButton onClick={() => onEdit(av)} testid={`c360-best-${av}-edit`} /></span>
      </div>
      <p className={`text-sm mt-2 leading-relaxed ${x.r === "unknown" ? "text-zinc-500" : "text-zinc-300"}`} data-testid={`c360-best-${av}-why`}>{mr ? `KOL Radar's reading (${rWord(x.r)}): ${why}` : why}</p>
      {mr && (
        <div className="mt-2 text-xs text-cyan-200" data-testid={`c360-best-${av}-mine`}>
          <p>Not KOL Radar's verdict: {changedLine(m, x.r)}.</p>
          {m.note && <p className="text-cyan-200/75 mt-1">Your note: "{m.note}"</p>}
        </div>
      )}
      {x.r !== "unknown" && <div className="mt-3"><TileRow av={av} d={d} compact /></div>}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-auto pt-4">
        <span className="mono text-[11px] text-zinc-500" data-testid={`c360-best-${av}-foot`}>{footer(av, d, bots).join(" · ")}</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(av); }} className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 rounded-md px-2.5 py-1" data-testid={`c360-best-${av}-open`}>Open {VIEW_TAB[av]} <ArrowRight size={12} /></button>
      </div>
    </div>
  );
}

export function BestAvenues({ d, onOpen, onEdit }) {
  const bots = useBots(d);
  return (
    <section data-testid="c360-best">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Best avenues · strongest first</span>
        <span className="text-[11px] text-zinc-600" data-testid="c360-best-hint">KOL Radar's verdicts · right-click one (or ✎) to change it</span>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{rankedAvenues(d).map((av, i) => <AvenueCard key={av} av={av} i={i} d={d} bots={bots} onOpen={onOpen} onEdit={onEdit} />)}</div>
    </section>
  );
}
